/**
 * Stage Chatbot-2 Phase 2:Chatbot admin API client。
 *
 * 包装 /api/chatbot/* 后端 endpoint;不在这里做业务逻辑,只负责请求和类型。
 *
 * 不做:
 * - Widget / public API client(那是 Phase 3)
 * - 任何 fallback mock — 真实数据 only,失败就抛 ApiError
 */

import { apiFetch } from "../../../api/client";

// ============================================================
// Types — 与 backend services/chatbot_widget_service / chatbot_knowledge_service 同步
// ============================================================

export type ChatbotSettings = {
  id: string;
  tenant_id: string;
  name: string;
  welcome_message: string;
  fallback_message: string;
  fallback_image_url: string;
  show_contact_form: boolean;
  notification_email: string;
  // Phase 4 公开 widget 配置字段
  placeholder: string;
  theme_color: string;
  title: string;
  allowed_domains: string[];
  is_active: boolean;
};

export type KnowledgeDocument = {
  id: string;
  tenant_id: string;
  source_type: "wp_post" | "wp_page" | "manual_text";
  source_url: string | null;
  source_id: string | null;
  title: string;
  status: "pending" | "processing" | "ready" | "failed" | "disabled" | "unindexed";
  source_authority: number;
  last_error: string | null;
  /** 本文。manual_text 类型存在;wp_post/wp_page 通常 null(走 WP 拉取,不存 DB) */
  content?: string | null;
  content_hash?: string;
  chunk_count?: number;
  created_at: string | null;
  updated_at: string | null;
  created_by?: string | null;
};

export type KnowledgeListResult = {
  items: KnowledgeDocument[];
};

export type ImportResult = {
  created: number;
  skipped: number;
  total_seen: number;
};

export type ChatbotAuditSession = {
  id: string;
  tenant_id: string;
  widget_id: string;
  status: string;
  page_url: string | null;
  ip_hash: string | null;
  ip_hash_short: string;
  message_count: number;
  user_message_count: number;
  bot_message_count: number;
  blocked: boolean;
  last_message_at: string | null;
  created_at: string | null;
  expires_at: string | null;
};

export type ChatbotAuditMessage = {
  id: string;
  session_id: string;
  tenant_id: string;
  role: "user" | "bot" | "assistant" | string;
  content: string;
  source_refs: unknown | null;
  input_tokens: number | null;
  output_tokens: number | null;
  created_at: string | null;
};

export type ChatbotVisitorBlock = {
  ip_hash: string;
  ip_hash_short: string;
  reason: string | null;
  expires_at: string | null;
  is_active: boolean;
};

// ============================================================
// Settings endpoints
// ============================================================

export const getSettings = () =>
  apiFetch<ChatbotSettings>("/chatbot/settings");

export const patchSettings = (body: Partial<ChatbotSettings>) =>
  apiFetch<ChatbotSettings>("/chatbot/settings", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

// ============================================================
// Phase 4:Widget 一括管理(create / get)
// ============================================================

export type WidgetCreateResult = {
  id: string;
  tenant_id: string;
  name: string;
  /** 一次性原文明文,仅 create 响应出现;后续 GET 不再含 */
  widget_key_plain_one_time: string;
  /** prefix(4) + '***' + last(4);可持久显示 */
  widget_key_masked: string;
  created_at: string;
};

export const createWidget = (name: string) =>
  apiFetch<WidgetCreateResult>("/chatbot/widget", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });

export const getWidget = () => apiFetch<ChatbotSettings>("/chatbot/widget");

// Phase 4.12: rotate-key(旧 widget_key 失效,生成新明文 widget_key)
export const rotateWidgetKey = () =>
  apiFetch<{ widget_key_plain_one_time: string; widget_key_masked: string }>(
    "/chatbot/widget/rotate-key",
    { method: "POST" }
  );


// ============================================================
// Knowledge endpoints
// ============================================================

export const listKnowledge = (status?: string) => {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiFetch<KnowledgeListResult>(`/chatbot/knowledge${q}`);
};

export const getKnowledge = (id: string) =>
  apiFetch<KnowledgeDocument>(`/chatbot/knowledge/${id}`);

export const createKnowledge = (body: { title: string; content: string }) =>
  apiFetch<KnowledgeDocument>("/chatbot/knowledge", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export const reindexKnowledge = (id: string) =>
  apiFetch<KnowledgeDocument>(`/chatbot/knowledge/${id}/reindex`, {
    method: "POST",
  });

export const patchKnowledge = (
  id: string,
  body: { title?: string; enabled?: boolean },
) =>
  apiFetch<KnowledgeDocument>(`/chatbot/knowledge/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export const deleteKnowledge = (id: string) =>
  apiFetch<{ deleted: string }>(`/chatbot/knowledge/${id}`, {
    method: "DELETE",
  });

export type KnowledgeBulkAction =
  | "reindex"
  | "enable"
  | "disable"
  | "delete"
  | "clear_index";

export const bulkKnowledge = (documentIds: string[], action: KnowledgeBulkAction) =>
  apiFetch<{
    action: KnowledgeBulkAction;
    requested: number;
    succeeded: number;
    failed: number;
    errors: Array<{ document_id: string; code: string; message: string }>;
  }>("/chatbot/knowledge/bulk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ document_ids: documentIds, action }),
  });

export const importWpPosts = () =>
  apiFetch<ImportResult>("/chatbot/knowledge/import/wp-posts", {
    method: "POST",
  });

export const importWpPages = () =>
  apiFetch<ImportResult>("/chatbot/knowledge/import/wp-pages", {
    method: "POST",
  });

// ============================================================
// Audit endpoints
// ============================================================

export const listAuditSessions = (limit = 50) =>
  apiFetch<{ items: ChatbotAuditSession[] }>(
    `/chatbot/audit/sessions?limit=${encodeURIComponent(String(limit))}`,
  );

export const getAuditSessionMessages = (sessionId: string) =>
  apiFetch<{
    session: ChatbotAuditSession;
    messages: ChatbotAuditMessage[];
  }>(`/chatbot/audit/sessions/${sessionId}/messages`);

export const blockAuditVisitor = (
  ipHash: string,
  body: { reason?: string; expires_at?: string | null } = {},
) =>
  apiFetch<ChatbotVisitorBlock>("/chatbot/audit/blocks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ip_hash: ipHash, ...body }),
  });

export const unblockAuditVisitor = (ipHash: string) =>
  apiFetch<ChatbotVisitorBlock>(
    `/chatbot/audit/blocks/${encodeURIComponent(ipHash)}`,
    { method: "DELETE" },
  );

// ============================================================
// Phase 2.5:Retrieval(検索テスト)endpoint
// ============================================================

export type SearchHit = {
  chunk_id: string;
  document_id: string;
  document_title: string;
  source_type: "wp_post" | "wp_page" | "manual_text";
  source_id: string | null;
  source_url: string | null;
  chunk_index: number;
  heading_path: string;
  content_preview: string;
  score: number;
};

export type SearchResult = {
  results: SearchHit[];
};

export const searchKnowledge = (question: string, topK = 5) =>
  apiFetch<SearchResult>("/chatbot/knowledge/search", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question, top_k: topK }),
  });

// ============================================================
// Phase 3:RAG 回答テスト — SSE streaming endpoint
// ============================================================

export type AnswerStreamEvent =
  | { kind: "candidates"; hits: SearchHit[] }
  | { kind: "delta"; text: string }
  | {
      kind: "done";
      status: string;
      final_text: string;
      fallback: boolean;
    }
  | { kind: "error"; code: string; message: string };

/** 调用 POST /api/chatbot/knowledge/answer-stream,产出按 SSE event 流的事件。

  - 用 fetch + reader(因为 EventSource 是 GET-only)
  - 解析 `\nevent: <kind>\ndata: <json>\n\n` wire format
  - 出错(包括 HTTP 4xx/5xx)在 generator 里抛 — caller `try/catch`
*/
export async function* answerStream(
  question: string,
  topK = 5,
  promptTemplateId = "standard_qa",
  signal?: AbortSignal,
): AsyncGenerator<AnswerStreamEvent> {
  const res = await fetch("/api/chatbot/knowledge/answer-stream", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      question,
      top_k: topK,
      prompt_template_id: promptTemplateId,
    }),
    signal,
  });

  if (!res.ok) {
    // 启动前一次性错误(JSON envelope)
    const text = await res.text();
    let detail = res.statusText;
    try {
      const data = JSON.parse(text);
      detail = data?.error?.message ?? text;
    } catch {
      // ignore
    }
    throw new Error(`RAG answer endpoint ${res.status}: ${detail}`);
  }
  if (!res.body) {
    throw new Error("RAG answer endpoint returned no body");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  // 解析 SSE 一块 wire(event: ... data: ... 一起)
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // 处理 buffer 里的完整 events(buffer 内可能含多个 event)
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) >= 0) {
      const rawEv = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);

      let evKind = "";
      const dataLines: string[] = [];
      for (const line of rawEv.split("\n")) {
        if (line.startsWith("event: ")) {
          evKind = line.slice("event: ".length).trim();
        } else if (line.startsWith("data: ")) {
          dataLines.push(line.slice("data: ".length));
        }
      }
      if (!evKind) continue;
      const dataText = dataLines.join("\n");
      let parsed: any = {};
      try {
        parsed = JSON.parse(dataText);
      } catch {
        parsed = {};
      }

      switch (evKind) {
        case "candidates":
          yield {
            kind: "candidates",
            hits: Array.isArray(parsed.hits) ? parsed.hits : [],
          };
          break;
        case "delta":
          yield { kind: "delta", text: String(parsed.text ?? "") };
          break;
        case "done":
          yield {
            kind: "done",
            status: String(parsed.status ?? "succeeded"),
            final_text: String(parsed.final_text ?? ""),
            fallback: Boolean(parsed.fallback),
          };
          return; // done 后流关闭
        case "error":
          yield {
            kind: "error",
            code: String(parsed.code ?? "AUTH_AI_FAILED"),
            message: String(parsed.message ?? "AI エンジンでエラーが発生しました。"),
          };
          return; // error 后流关闭
        default:
          // 静默忽略未知事件
          break;
      }
    }
  }
}
